import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GeolocationData {
  ip_address?: string;
  country?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
}

/**
 * Determines default language based on user's country
 * Portuguese for Brazil/Portugal, English for all others
 */
export function getDefaultLanguageFromCountry(country?: string): 'en' | 'pt-BR' {
  if (!country) return 'en';
  
  const portugueseCountries = ['Brazil', 'Portugal', 'BR', 'PT', 'Brasil'];
  return portugueseCountries.some(c => 
    country.toLowerCase().includes(c.toLowerCase())
  ) ? 'pt-BR' : 'en';
}

interface GeolocationContextType {
  geolocation: GeolocationData | null;
  loading: boolean;
  suggestedLanguage: 'en' | 'pt-BR';
}

const GeolocationContext = createContext<GeolocationContextType>({
  geolocation: null,
  loading: true,
  suggestedLanguage: 'en',
});

export function GeolocationProvider({ children }: { children: ReactNode }) {
  const [geolocation, setGeolocation] = useState<GeolocationData | null>(() => {
    // Try to get cached data from sessionStorage
    const cached = sessionStorage.getItem("geolocation_data");
    const cacheTime = sessionStorage.getItem("geolocation_cache_time");
    
    if (cached && cacheTime) {
      const age = Date.now() - parseInt(cacheTime);
      // Cache valid for 24 hours
      if (age < 24 * 60 * 60 * 1000) {
        try {
          const parsed = JSON.parse(cached);
          // Clear old format with "Hidden" IP address
          if (parsed.ip_address === "Hidden" || parsed.ip_address === "Unknown") {
            sessionStorage.removeItem("geolocation_data");
            sessionStorage.removeItem("geolocation_cache_time");
            return null;
          }
          return parsed;
        } catch {
          sessionStorage.removeItem("geolocation_data");
          sessionStorage.removeItem("geolocation_cache_time");
        }
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [suggestedLanguage, setSuggestedLanguage] = useState<'en' | 'pt-BR'>(() => {
    // Calculate initial suggested language from cached data
    if (geolocation?.country) {
      return getDefaultLanguageFromCountry(geolocation.country);
    }
    return 'en';
  });

  // Update suggested language when geolocation changes
  useEffect(() => {
    if (geolocation?.country) {
      const lang = getDefaultLanguageFromCountry(geolocation.country);
      setSuggestedLanguage(lang);
    }
  }, [geolocation]);

  useEffect(() => {
    // If we already have data, don't fetch again
    if (geolocation) {
      setLoading(false);
      return;
    }

    const fetchIPData = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      try {
        const { data, error } = await supabase.functions.invoke('get-client-info', {
          body: {},
          signal: controller.signal
        });
        
        if (error) throw error;
        
        if (data) {
          const sanitizedGeo = {
            country: data.geolocation?.country,
            city: data.geolocation?.city,
            region: data.geolocation?.region,
            timezone: data.geolocation?.timezone,
          };
          setGeolocation(sanitizedGeo);
          sessionStorage.setItem("geolocation_data", JSON.stringify(sanitizedGeo));
          sessionStorage.setItem("geolocation_cache_time", Date.now().toString());
        } else {
          throw new Error('No data received');
        }
      } catch (error) {
        console.error("Failed to fetch IP/geolocation:", error);
        const fallbackData = { country: "Unknown" };
        setGeolocation(fallbackData);
        sessionStorage.setItem("geolocation_data", JSON.stringify(fallbackData));
        sessionStorage.setItem("geolocation_cache_time", Date.now().toString());
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    fetchIPData();
  }, [geolocation]);

  return (
    <GeolocationContext.Provider value={{ geolocation, loading, suggestedLanguage }}>
      {children}
    </GeolocationContext.Provider>
  );
}

export function useGeolocation() {
  const context = useContext(GeolocationContext);
  if (context === undefined) {
    throw new Error("useGeolocation must be used within a GeolocationProvider");
  }
  return context;
}